import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import { useState } from 'react'
import { ActivityLogListQueries } from '@/queries/users/queries.ts'
import { useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  AcitivityLogList,
  SchemaAcitivityLogListList,
} from '@/queries/users/requests.ts'
import { cn } from '@/common/utilities.ts'
import HtmlRenderer from '@/common/html_renderer.tsx'
import { TimeIcon } from '@/common/icons/general.tsx'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'
import { TableToolBar } from '@/common/components/StackedTable'
import SearchInput from '@/common/components/SearchInput'
import MultipleSelector, {
  ListItem,
} from '@/common/components/MultipleSelector'
import { FilterIcon } from '@/common/icons/general'
import { historyType } from '@/app/common/constants'

interface HistoryProps {
  user_id: number
  user_type_id: number
}
// eslint-disable-next-line
export const TABLE_FILTERS = [
  { id: 1, name: 'Add' },
  { id: 2, name: 'Update' },
  { id: 3, name: 'Delete' },
  { id: 4, name: 'View Page' },
  { id: 5, name: 'Resolve Appeal' },
  { id: 6, name: 'Upload Document' },
  { id: 7, name: 'Withdraw' },
  { id: 8, name: 'Register' },
  { id: 9, name: 'Start' },
  { id: 10, name: 'Failed' },
  { id: 11, name: 'Multiple Updates' },
  { id: 12, name: 'View Document' },
  { id: 13, name: 'Flag Decision' },
  { id: 14, name: 'Submitted' },
  { id: 15, name: 'Accepted (Attestation)' },
]

const InitialActivityLogList: SchemaAcitivityLogListList = [] as const
const HistoryView = ({ user_id, user_type_id }: HistoryProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10)
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([])
  const [search, setsearch] = useState('')

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // const convertToProgramTime = (created_datetime: string) => {
  //   let date = new Date(created_datetime + 'Z')
  //   date = toZonedTime(date, 'UTC')
  //   return format(date, 'MMM dd, yyyy h:mm aa');
  // }

  const convertToProgramTime = (created_datetime: string) => {
    let date = new Date(created_datetime)
    if (
      selectedProgramYear?.global_timezone_code != undefined &&
      selectedProgramYear.global_timezone_code.trim() != ''
    ) {
      date = new Date(created_datetime + 'Z')
      date = toZonedTime(date, selectedProgramYear.global_timezone_code)
    }
    return format(date, 'MM/dd/yyyy, h:mm aa')
  }

  const ActivityLogListQuery = useQuery(
    ActivityLogListQueries.getPaginatedActivityLogByUserId(
      recordsPerPage,
      currentPage,
      user_id,
      user_type_id,
      search,
      selectedFilter.map((filter) => filter.id).join(','),
      {
        staleTime: 0,
        refetchOnMount: true,
      }
    )
  )

  // Loading Table
  const columnHelper = createColumnHelper<AcitivityLogList>()
  const columns = [
    columnHelper.accessor('display_content', {
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="relative pb-8">
            <span
              aria-hidden="true"
              className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
            ></span>
            <div className="relative flex space-x-3">
              <div>
                <span className="text-primary bg-white flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-primary">
                  <i className="ki-message-notify ki-filled"></i>
                </span>
              </div>
              {row.created_datetime && (
                <div>
                  <div>
                    <HtmlRenderer htmlContent={info.getValue() ?? ''} />{' '}
                  </div>
                  <div className="mt-2 flex gap-1 items-center ">
                    <TimeIcon />
                    <div className="text-gray-400 text-sm">
                      {convertToProgramTime(row.created_datetime)}
                    </div>
                  </div>{' '}
                </div>
              )}
            </div>
          </div>
        )
      },
    }),
  ]

  useEffect(() => {
    if (
      !ActivityLogListQuery.isLoading &&
      ActivityLogListQuery.data === undefined
    ) {
      setCurrentPage(1)
    }
  }, [ActivityLogListQuery.data, currentPage, ActivityLogListQuery.isLoading])

  const table = useReactTable({
    data: ActivityLogListQuery.data?.data ?? InitialActivityLogList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: ActivityLogListQuery.data?.last_page,
  })

  const filterOptions = () => {
    if (user_type_id == historyType.provider)
      return TABLE_FILTERS.filter((option) =>
        [1, 2, 4, 8, 9, 13].includes(option.id)
      )
    return TABLE_FILTERS
  }

  return (
    <>
      <Card className="mt-4">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-md font-semibold text-gray-900">
            History
          </div>
          <TableToolBar>
            <SearchInput
              id="table_search"
              placeholder="Search History"
              value={search}
              onChange={(e) => setsearch(e.target.value)}
            />
            <MultipleSelector
              list={filterOptions()}
              selected={selectedFilter}
              setSelected={setSelectedFilter}
              icon={<FilterIcon className="text-primary" />}
              className="pl-2"
            />
          </TableToolBar>
        </CardHeader>
        <CardContent className="p-10">
          <table>
            {(function () {
              if (ActivityLogListQuery.error) {
                return (
                  <tbody>
                    <tr>
                      <td colSpan={5} className="text-center p-2">
                        {!Array.isArray(ActivityLogListQuery?.data) ||
                        ActivityLogListQuery.data.length === 0
                          ? 'No activiy log found.'
                          : null}
                      </td>
                    </tr>
                  </tbody>
                )
              } else {
                return (
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={cn(
                              'w-[15%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2 max-w-40 text-ellipsis'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )
              }
            })()}
          </table>
          {/* <NotificationFeed timeline={selectedData} /> */}
        </CardContent>
        <CardFooter className="p-0">
          <Pagination
            itemsPerPage={recordsPerPage}
            currentPage={currentPage}
            totalRows={ActivityLogListQuery.data?.total ?? 0}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </CardFooter>
      </Card>
    </>
  )
}

export default HistoryView
